// Basketball — Analysis (CLV Track · No-Bet Report · Leagues). Ported from the
// CLAUDE DESIGN prototype (bb-analysis.jsx → ClvScreen / NoBetsScreen /
// LeaguesScreen). HONEST by construction:
//   • CLV   — always the empty-ledger + ghost-preview-chart pattern (the ledger
//             is empty by design until basketball picks settle).
//   • No-Bet — dormant ghost rows until a future declined-markets RPC lands.
//   • Leagues — static coverage config (never dormant).

import { AwaitPanel, BbHead } from "./_bbkit.tsx";

// ── CLV Track ───────────────────────────────────────────────────
function ClvRow({ k, v, sub }: { k: string; v: string; sub: string }): JSX.Element {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "baseline", gap: 14, paddingBottom: 12, borderBottom: "1px dashed var(--border-faint)" }}>
      <div>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.14em", color: "var(--text-tertiary)", textTransform: "uppercase" }}>{k}</div>
        <div style={{ fontSize: 12, color: "var(--text-quaternary)", marginTop: 2 }}>{sub}</div>
      </div>
      <div style={{ fontFamily: "var(--bb-display)", fontSize: 28, letterSpacing: "0.01em", color: "var(--text)", fontVariantNumeric: "tabular-nums" }}>{v}</div>
    </div>
  );
}

export function ClvScreen(): JSX.Element {
  return (
    <div className="bb" data-screen-label="clv">
      <BbHead kicker="CLV TRACK · PRO+" title="Basketball" />

      <div className="bb-card" style={{ marginBottom: 24 }}>
        <div className="bb-card-body" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32 }}>
          <div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.18em", color: "var(--bb-orange-text)", textTransform: "uppercase", marginBottom: 12 }}>How CLV works here</div>
            <h3 style={{ fontFamily: "var(--bb-display)", fontSize: 36, letterSpacing: "0.01em", margin: "0 0 12px", fontWeight: 400 }}>CLV accrues once picks settle.</h3>
            <p style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.6, maxWidth: "52ch" }}>
              Every market we touch carries a real Pinnacle closing line. The CLV track fills as
              settled picks land in the unified CLV ledger — basketball joins football and tennis
              under one yardstick. We do not synthesize CLV. We earn it.
            </p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <ClvRow k="Markets tracked" v="3" sub="Moneyline · Spread · Total" />
            <ClvRow k="Closing lines stored" v="0 / 750" sub="Calibration window · season" />
            <ClvRow k="Sample qualified" v="—" sub="Awaiting settled picks" />
            <ClvRow k="Beat-the-close %" v="—" sub="Empty by design" />
            <ClvRow k="Last updated" v="Today · 16:02" sub="Server-side" />
          </div>
        </div>
      </div>

      <AwaitPanel
        kicker="LEDGER EMPTY"
        title="The CLV ledger is empty for basketball — and that's the point."
        body="The layer is dormant. No picks have settled, so no CLV has been recorded. The moment slip-live publication opens, this surface starts filling with the closing-line deltas we actually beat."
        icon="trend"
        ghosts={6}
      />

      {/* Ghost preview chart — what CLV will look like when it fills */}
      <div className="bb-clv-ghost">
        <div className="bb-clv-ghost-head">
          <div>
            <div className="k">PREVIEW · not live</div>
            <div className="t">What this chart fills with</div>
          </div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-tertiary)", letterSpacing: "0.08em" }}>
            Cumulative beat-the-close % · weekly rollup
          </div>
        </div>
        <div className="bb-clv-ghost-svg">
          <svg viewBox="0 0 800 240" preserveAspectRatio="none">
            <line x1="40" x2="780" y1="120" y2="120" stroke="var(--border)" strokeWidth="1" strokeDasharray="3 3" />
            <g fontFamily="var(--font-mono)" fontSize="10" fill="var(--text-quaternary)">
              <text x="8" y="44">+5%</text>
              <text x="8" y="124">0</text>
              <text x="8" y="204">−5%</text>
            </g>
            <path d="M 40 124 L 100 118 L 160 110 L 220 116 L 280 96 L 340 86 L 400 78 L 460 80 L 520 68 L 580 72 L 640 60 L 700 56 L 780 50" fill="none" stroke="var(--bb-orange)" strokeWidth="2" strokeDasharray="4 4" opacity="0.55" />
            <path d="M 40 124 L 100 118 L 160 110 L 220 116 L 280 96 L 340 86 L 400 78 L 460 80 L 520 68 L 580 72 L 640 60 L 700 56 L 780 50 L 780 220 L 40 220 Z" fill="var(--bb-orange-soft)" opacity="0.4" />
            <g fontFamily="var(--font-mono)" fontSize="9" fill="var(--text-quaternary)" textAnchor="middle">
              {["W1", "W2", "W3", "W4", "W5", "W6", "W7", "W8", "W9", "W10", "W11", "W12", "W13"].map((w, i) => (
                <text key={i} x={40 + i * (740 / 12)} y="232">{w}</text>
              ))}
            </g>
          </svg>
          <div className="bb-clv-ghost-overlay">
            <div className="lbl"><strong>+ 2.4%</strong>&nbsp;·&nbsp;projected after sample qualified</div>
          </div>
        </div>
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border-faint)", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
          {[
            { k: "WHEN", v: "After Step 03" },
            { k: "UPDATE", v: "Weekly · Mon 06:00 GMT" },
            { k: "SOURCE", v: "Pinnacle close · unified ledger" },
            { k: "AUDIT", v: "Public · per-pick line stored" },
          ].map((d, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", gap: 2, paddingLeft: i > 0 ? 14 : 0, borderLeft: i > 0 ? "1px solid var(--border-faint)" : "none" }}>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 9.5, color: "var(--text-tertiary)", letterSpacing: "0.14em", textTransform: "uppercase" }}>{d.k}</span>
              <span style={{ fontSize: 13, color: "var(--text)", fontWeight: 500 }}>{d.v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── No-Bet Report ───────────────────────────────────────────────
// No declined-markets RPC yet — the report is dormant by construction today.
export function NoBetsScreen(): JSX.Element {
  return (
    <div className="bb" data-screen-label="no-bets">
      <BbHead kicker="NO-BET REPORT" title="Basketball" />
      <div className="bb-await-grand">
        <div className="k">FILLS WHEN LIVE · NO-BET REPORT</div>
        <h3>The No-Bet Report fills once the card runs live.</h3>
        <p>Every market the engine declined today, with the reason. We publish our no-bets so the discipline reads as loud as the picks — what we sit out is itself the strategy.</p>
        <div className="bb-ghost-nobets" aria-hidden="true">
          {["SPREAD", "TOTAL", "MONEYLINE", "SPREAD"].map((mk, i) => (
            <div key={i} className="gh-row">
              <div className="gh-match" />
              <div className="gh-market">{mk}</div>
              <div className="gh-reason" />
            </div>
          ))}
          <div className="gh-overlay">PREVIEW · declined markets fill here</div>
        </div>
      </div>
    </div>
  );
}

// ── Leagues ─────────────────────────────────────────────────────
// Static coverage config (no RPC). Region order preserved from the prototype.
const LEAGUES: ReadonlyArray<{ reg: string; name: string; games: string }> = [
  { reg: "AMERICAS", name: "NBA", games: "1,230 · season" },
  { reg: "AMERICAS", name: "WNBA", games: "160 · season" },
  { reg: "AMERICAS", name: "NCAA D-I", games: "5,400 · season" },
  { reg: "EUROPE", name: "EuroLeague", games: "348 · season" },
  { reg: "EUROPE", name: "Liga ACB · ES", games: "306 · season" },
  { reg: "EUROPE", name: "LBA · IT", games: "240 · season" },
  { reg: "EUROPE", name: "LNB Pro A · FR", games: "270 · season" },
  { reg: "ASIA-PAC", name: "NBL · AU", games: "152 · season" },
  { reg: "ASIA-PAC", name: "B.League · JP", games: "600 · season" },
];

export function LeaguesScreen(): JSX.Element {
  const regions = new Map<string, typeof LEAGUES[number][]>();
  for (const l of LEAGUES) {
    const arr = regions.get(l.reg) ?? [];
    arr.push(l);
    regions.set(l.reg, arr);
  }

  return (
    <div className="bb" data-screen-label="leagues">
      <BbHead kicker="LEAGUES" title="Basketball" />

      <p style={{ fontSize: 14, color: "var(--text-secondary)", maxWidth: "64ch", lineHeight: 1.55, marginBottom: 24 }}>
        Basketball coverage map. The engine reads every closing line across every league below —
        not all are slip-live, none are off the radar.
      </p>

      {Array.from(regions.entries()).map(([reg, leagues]) => (
        <div key={reg} style={{ marginBottom: 22 }}>
          <div className="bb-section-title">
            <h3>{reg}</h3>
            <div className="sub">{leagues.length} leagues</div>
          </div>
          <div className="bb-leagues-map">
            {leagues.map((l, i) => (
              <div key={i} className="bb-league-row">
                <div className="reg">{l.reg}</div>
                <div className="nm">{l.name}</div>
                <div className="ct">{l.games}</div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div style={{ marginTop: 24, padding: "18px 22px", background: "var(--bg-elev)", border: "1px solid var(--border)", borderRadius: 10, fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.08em", color: "var(--text-tertiary)" }}>
        Calibration sample is built per league per market. A league appears in the slip flow only
        after it clears Step 03 (close-line history threshold).
      </div>
    </div>
  );
}
